/*
 * Mock data for Logistics Collaboration Dashboard
 * Toggle via MOCK_LOGISTICS.
 *
 * When MOCK_LOGISTICS = true, LogisticsCollaborationDashboard.fetchData bypasses axios:
 *   Pre-Schedule view  → MOCK_PRE_SCHEDULE_RESPONSE  (batchesList / totalNoOfRecords / numberOfPages)
 *   Post-Schedule view → MOCK_SHIPMENT_RESPONSE.postSchedule  (shipmentList / totalNoOfRecords / noOfPages)
 *   Post-Delivery view → MOCK_SHIPMENT_RESPONSE.postDelivery  (shipmentList / totalNoOfRecords / noOfPages)
 */

export const MOCK_LOGISTICS = false;

// Scoped demo flag — prefills ONLY the Shipment Schedule ID form from the Excel
// fixture (MOCK_SCHEDULE_FORM_DATA / MOCK_SHIPMENT_IDS). Independent of
// MOCK_LOGISTICS, so the rest of the Logistics Dashboard keeps using the live backend.
export const MOCK_SCHEDULE_FORM = true;

export const MOCK_FILTERS = {
  cmos:              ['Cipla - Goa', 'Lupin - Pithampur', 'Aurobindo - Hyderabad', 'Patheon - Toronto', 'Oxford Biomedica - Oxford'],
  consignees:        ['DHL Supply Chain', 'Kuehne+Nagel', 'DB Schenker', 'CEVA Logistics'],
  freightForwarders: ['DHL Global Forwarding', 'Expeditors', 'Geodis', 'Kuehne+Nagel'],
  brands:            ['Baraclude', 'Opdivo', 'Reyataz', 'Sustiva', 'Eliquis', 'Entecavir'],
  sponsors:          ['BMS', 'Pfizer', 'Merck'],
};

export const MOCK_QUICK_SUMMARY = {
  marketPOsReady:     8,
  marketPOsScheduled: 12,
  marketPOsInTransit: 2,
  marketPOsDelivered: 8,
  newActions:         8,
  completed:          8,
  delayed:            3,
};

// ─── Shared document data (used by all post-schedule/post-delivery shipments) ──
const CUSTOM_FILES_LIST = [
  {
    lastUpdatedDate:        '2026-06-03T09:33:10.816',
    documentCategoryType:   'Post-Shipment Documents',
    customFileType:         'Proforma Invoice',
    schrockenCustomFileID:  '122',
    customFileHash:         '1CF3BDD0A3F682E03F81CFB1F3A5D888225112A342DFD00411C4A4189F68AC5F',
    customFileName:         'OXB-OXB-ProformaInvoice-4500003373-C03373-7a0bc4.pdf',
    customFileDescription:  null,
    markedForDelete:        null,
  },
];

const KVP_JSON = {
  markAsReviewed: true,
  s3_location:    's3://bms-processed-files/OXB/OXB/4500003373-C03373/OXB-OXB-ProformaInvoice-4500003373-C03373-7a0bc4.pdf',
  folder_name:    'Oxford Biomedica',
  invoice_comments:      { confidence: '99.658203125',         value: 'RAD05092025 3919519200-PKG428-25F', isMandatory: false },
  net_weight:            { confidence: '99.3701171875',         value: '44,000',       isMandatory: false },
  total_no_of_packages:  { confidence: '99.970703125',          value: '1',            isMandatory: false },
  invoice_date:          { confidence: '98.125',                value: '03-MAY-2026',  isMandatory: false },
  cust_po_number:        { confidence: '99.970703125',          value: '4500003373',   isMandatory: false },
  site_name:             'Oxford Biomedica',
  noOfFieldsToBeReviewed: 0,
  filename:              'OXB-OXB-ProformaInvoice-4500003373-C03373-7a0bc4.pdf',
  shipment_mode:         { confidence: '98.7451171875',         value: 'Truck',        isMandatory: false },
  noOfFieldsReviewed:    0,
  delivery_number:       { confidence: '99.970703125',          value: '8019366122',   isMandatory: false },
  gross_weight:          { confidence: '99.37908935546875',      value: '69,000',       isMandatory: false },
  invoice_number:        { confidence: '99.970703125',          value: '2590020157',   isMandatory: false },
  document_type:         'Proforma Invoice',
};

const KVP_JSON_EDITED = {};

// ─── Pre-Schedule helpers ─────────────────────────────────────────────────────
let _batchId = 60000;
const nextBatchId = () => ++_batchId;

const makeRow = (overrides) => ({
  // Identifiers
  scheduleAgreementNumber:          '',
  schrockenDeliveryScheduleBatchID: nextBatchId(),
  vendorBatchNumber:                '',
  fgMaterialCode:                   '',
  fgMaterialName:                   '',
  brand:                            '',
  producedQuantity:                 0,
  batchUOM:                         'EA',
  plNumber:                         null,
  // Status
  statusID:                         62,
  statusDescription:                'Shipment Authorized',
  // Sponsor / Shipment IDs
  sponsorShipmentID:                null,
  freightForwarderScheduleID:       null,
  // Dates
  plannedDispatchDate:              null,
  actualDispatchDate:               null,
  dispatchDate:                     null,
  estimatedArrivalDate:             null,
  manufacturingDate:                '2026-01-15T00:00',
  expiryDate:                       '2027-12-31T00:00',
  // Cargo
  netWeight:                        null,
  grossWeight:                      '0',
  noOfPallets:                      '1',
  tempRangeAndSetPoint:             'Store 15 - 25 deg C',
  // Vendor / Destination
  deliveryAddresseeCountry:         'United Kingdom',
  deliveryAddresseeOrgName:         'BMS London Distribution',
  vendorName:                       'Oxford Biomedica',
  vendorMosymphonyID:               3,
  vendorPlant:                      'Oxford Biomedica - Oxford',
  // ERP
  receivingStorageLocationIDsFromSponsorERP: null,
  receivingPlantIDFromSponsorERP:            null,
  materialStorageLocationIDsFromSponsorERP:  null,
  materialPlantIDFromSponsorERP:             null,
  aslNumber:                        'ASL-1710-001',
  // Documents
  kvpJson:         null,
  kvpJsonEdited:   null,
  customFilesList: [],
  overlays:        [],
  ...overrides,
  // API field aliases — auto-computed from base fields if not explicitly provided
  readyForDispatchQuantity: overrides.readyForDispatchQuantity !== undefined
    ? overrides.readyForDispatchQuantity
    : (overrides.producedQuantity || 0),
  uomQA: overrides.uomQA !== undefined
    ? overrides.uomQA
    : (overrides.batchUOM || 'EA'),
});

// ─── Pre-Schedule rows (fetchDataForPreScheduleView → batchesList) ─────────────
const PRE_SCHEDULE_ROWS = [
  makeRow({
    scheduleAgreementNumber: '4500006396', vendorBatchNumber: 'OPD6396F',
    fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
    brand: 'Opdivo', producedQuantity: 1800, batchUOM: 'EA',
    plNumber: 'PL-02153643', statusID: 62, statusDescription: 'Shipment Authorized',
    netWeight: '140 KG', grossWeight: '168.500', noOfPallets: '18',
    tempRangeAndSetPoint: 'Store 2 - 8 deg C',
    manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-12-31T00:00',
    plannedDispatchDate: '2026-07-10T06:50:38.673162', dispatchDate: null, actualDispatchDate: null,
    estimatedArrivalDate: '2026-07-20T06:50:38.673162',
    deliveryAddresseeCountry: 'United Kingdom',
    deliveryAddresseeOrgName: 'BMS London Distribution',
    vendorName: 'Oxford Biomedica', vendorMosymphonyID: 3, vendorPlant: 'Oxford Biomedica - Oxford',
    aslNumber: 'ASL-1710-003', sponsorShipmentID: null, freightForwarderScheduleID: null,
    receivingStorageLocationIDsFromSponsorERP: 'SL-UK-001',
    receivingPlantIDFromSponsorERP: 'PLANT-UK-001',
    materialStorageLocationIDsFromSponsorERP: 'MSL-UK-001',
    materialPlantIDFromSponsorERP: 'MPLANT-UK-001',
  }),
  makeRow({
    scheduleAgreementNumber: '4500006397', vendorBatchNumber: 'PQR6397G',
    fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
    brand: 'Baraclude', producedQuantity: 923, batchUOM: 'EA',
    plNumber: 'PL-02153644', statusID: 62, statusDescription: 'Shipment Authorized',
    netWeight: '75 KG', grossWeight: '90.000', noOfPallets: '10',
    tempRangeAndSetPoint: 'Store 15 - 25 deg C',
    manufacturingDate: '2026-02-05T00:00', expiryDate: '2027-10-31T00:00',
    plannedDispatchDate: '2026-07-08T06:50:38.673162', dispatchDate: null, actualDispatchDate: null,
    estimatedArrivalDate: '2026-07-18T06:50:38.673162',
    deliveryAddresseeCountry: 'Netherlands',
    deliveryAddresseeOrgName: 'BMS Amsterdam Warehouse',
    vendorName: 'Oxford Biomedica', vendorMosymphonyID: 3, vendorPlant: 'Oxford Biomedica - Oxford',
    aslNumber: 'ASL-1710-001', sponsorShipmentID: null, freightForwarderScheduleID: null,
    receivingStorageLocationIDsFromSponsorERP: 'SL-NL-001',
    receivingPlantIDFromSponsorERP: 'PLANT-NL-001',
    materialStorageLocationIDsFromSponsorERP: 'MSL-NL-001',
    materialPlantIDFromSponsorERP: 'MPLANT-NL-001',
  }),
  makeRow({
    scheduleAgreementNumber: '4500006398', vendorBatchNumber: 'ELI6398H',
    fgMaterialCode: '7820048', fgMaterialName: 'ELIQUIS TAB 5MG (60CT)',
    brand: 'Eliquis', producedQuantity: 2100, batchUOM: 'EA',
    plNumber: 'PL-02153645', statusID: 62, statusDescription: 'Shipment Authorized',
    netWeight: '105 KG', grossWeight: '125.000', noOfPallets: '22',
    tempRangeAndSetPoint: 'Store 15 - 25 deg C',
    manufacturingDate: '2026-03-01T00:00', expiryDate: '2028-02-28T00:00',
    plannedDispatchDate: '2026-07-12T06:50:38.673162', dispatchDate: null, actualDispatchDate: null,
    estimatedArrivalDate: '2026-07-22T06:50:38.673162',
    deliveryAddresseeCountry: 'Belgium',
    deliveryAddresseeOrgName: 'BMS Brussels Warehouse',
    vendorName: 'Oxford Biomedica', vendorMosymphonyID: 3, vendorPlant: 'Oxford Biomedica - Oxford',
    aslNumber: 'ASL-1710-004', sponsorShipmentID: null, freightForwarderScheduleID: null,
    receivingStorageLocationIDsFromSponsorERP: 'SL-BE-001',
    receivingPlantIDFromSponsorERP: 'PLANT-BE-001',
    materialStorageLocationIDsFromSponsorERP: 'MSL-BE-001',
    materialPlantIDFromSponsorERP: 'MPLANT-BE-001',
  }),
  makeRow({
    scheduleAgreementNumber: '4500006399', vendorBatchNumber: 'ENTC6399I',
    fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
    brand: 'Entecavir', producedQuantity: 3000, batchUOM: 'TS',
    plNumber: 'PL-02153646', statusID: 62, statusDescription: 'Shipment Authorized',
    netWeight: '200 KG', grossWeight: '240.000', noOfPallets: '30',
    tempRangeAndSetPoint: 'Store 15 - 25 deg C',
    manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-10-31T00:00',
    plannedDispatchDate: '2026-07-15T06:50:38.673162', dispatchDate: null, actualDispatchDate: null,
    estimatedArrivalDate: '2026-07-25T06:50:38.673162',
    deliveryAddresseeCountry: 'Germany',
    deliveryAddresseeOrgName: 'BMS Frankfurt Distribution Center',
    vendorName: 'Patheon - Toronto', vendorMosymphonyID: 5, vendorPlant: 'Patheon - Toronto',
    aslNumber: 'ASL-1710-002', sponsorShipmentID: null, freightForwarderScheduleID: null,
    receivingStorageLocationIDsFromSponsorERP: 'SL-DE-001',
    receivingPlantIDFromSponsorERP: 'PLANT-DE-001',
    materialStorageLocationIDsFromSponsorERP: 'MSL-DE-001',
    materialPlantIDFromSponsorERP: 'MPLANT-DE-001',
  }),
  makeRow({
    scheduleAgreementNumber: '4500006400', vendorBatchNumber: 'OPD6400J',
    fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
    brand: 'Opdivo', producedQuantity: 807, batchUOM: 'EA',
    plNumber: 'PL-02153647', statusID: 62, statusDescription: 'Shipment Authorized',
    netWeight: '58 KG', grossWeight: '70.000', noOfPallets: '8',
    tempRangeAndSetPoint: 'Store 2 - 8 deg C',
    manufacturingDate: '2026-02-10T00:00', expiryDate: '2028-01-31T00:00',
    plannedDispatchDate: '2026-07-18T06:50:38.673162', dispatchDate: null, actualDispatchDate: null,
    estimatedArrivalDate: '2026-07-28T06:50:38.673162',
    deliveryAddresseeCountry: 'United Kingdom',
    deliveryAddresseeOrgName: 'BMS London Distribution',
    vendorName: 'Oxford Biomedica', vendorMosymphonyID: 3, vendorPlant: 'Oxford Biomedica - Oxford',
    aslNumber: 'ASL-1710-003', sponsorShipmentID: null, freightForwarderScheduleID: null,
    receivingStorageLocationIDsFromSponsorERP: 'SL-UK-002',
    receivingPlantIDFromSponsorERP: 'PLANT-UK-001',
    materialStorageLocationIDsFromSponsorERP: 'MSL-UK-002',
    materialPlantIDFromSponsorERP: 'MPLANT-UK-001',
  }),
  makeRow({
    scheduleAgreementNumber: '4500006401', vendorBatchNumber: 'PQR6401K',
    fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (9BLP X 10) FR',
    brand: 'Baraclude', producedQuantity: 651, batchUOM: 'EA',
    plNumber: 'PL-02153648', statusID: 62, statusDescription: 'Shipment Authorized',
    netWeight: '50 KG', grossWeight: '60.000', noOfPallets: '7',
    tempRangeAndSetPoint: 'Store 15 - 25 deg C',
    manufacturingDate: '2026-01-28T00:00', expiryDate: '2027-12-31T00:00',
    plannedDispatchDate: '2026-07-20T06:50:38.673162', dispatchDate: null, actualDispatchDate: null,
    estimatedArrivalDate: '2026-07-30T06:50:38.673162',
    deliveryAddresseeCountry: 'Iceland',
    deliveryAddresseeOrgName: 'BMS Iceland Warehouse',
    vendorName: 'Cipla - Goa', vendorMosymphonyID: 2, vendorPlant: 'Cipla - Goa',
    aslNumber: 'ASL-1710-001', sponsorShipmentID: null, freightForwarderScheduleID: null,
    receivingStorageLocationIDsFromSponsorERP: 'SL-IS-001',
    receivingPlantIDFromSponsorERP: 'PLANT-IS-001',
    materialStorageLocationIDsFromSponsorERP: 'MSL-IS-001',
    materialPlantIDFromSponsorERP: 'MPLANT-IS-001',
  }),
];

// ─── Post-Schedule/Post-Delivery helpers ──────────────────────────────────────

const makeShipmentBatch = (overrides) => ({
  scheduleAgreementNumber:          '',
  schrockenDeliveryScheduleBatchID: nextBatchId(),
  fgMaterialCode:                   '',
  fgMaterialName:                   '',
  readyForDispatchQuantity:         0,
  uomQA:                            'EA',
  noOfPallets:                      '1',
  netWeight:                        null,
  grossWeight:                      '0',
  tempRangeAndSetPoint:             'Ambient',
  dispatchQuantity:                 0,
  dispatchUOM:                      'EA',
  manufacturingDate:                '2026-01-15T00:00',
  expiryDate:                       '2027-12-31T00:00',
  consigneeGRNNumber:               null,
  consigneeGRNGeneratedDate:        null,
  vendorBatchNumber:                '',
  scheduleLineItemNumber:           '',
  vendorPlant:                      '',
  storageLocation:                  '',
  ...overrides,
});

const makeShipmentRow = (overrides) => ({
  collabrixFGBatchShipmentID: null,
  sponsorShipmentID:          '',
  freightForwarderScheduleID: '',
  dispatchDate:               null,
  netWeight:                  null,
  temperature:                'Ambient',
  tempRangeAndSetPoint:       'Ambient',
  statusDescription:          '',
  statusID:                   null,
  estimatedArrivalDate:       null,
  plannedDispatchDate:        null,
  actualDispatchDate:         null,
  vendorShipmentID:           '',
  vendorName:                 '',
  showGenerateDocumentAction: false,
  customFilesList:            CUSTOM_FILES_LIST,
  kvpJson:                    KVP_JSON,
  kvpJsonEdited:              KVP_JSON_EDITED,
  batchesList:                [],
  shipmentEvents:             [],
  ...overrides,
});

// ─── Post-Schedule shipments (fetchShipmentDataForLogistics, view=postSchedule) ─
const POST_SCHEDULE_SHIPMENTS = [
  // 63 — Shipment Scheduled
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10001,
    sponsorShipmentID: 'PKG428-60A', freightForwarderScheduleID: '3919519201',
    plannedDispatchDate: '2026-05-25T06:50:38.673162', actualDispatchDate: null, dispatchDate: null,
    netWeight: '205 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 63, statusDescription: 'Shipment Scheduled',
    estimatedArrivalDate: '2026-06-05T06:50:38.673162', vendorShipmentID: 'VSI-60A',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-24T09:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-25T10:30:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550281500',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 1300, uomQA: 'EA', noOfPallets: '13',
        netWeight: '105 KG', grossWeight: '122.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1300, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6410A',
        manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-12-31T00:00',
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550281400',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (9BLP X 10) FR',
        readyForDispatchQuantity: 900, uomQA: 'EA', noOfPallets: '9',
        netWeight: '70 KG', grossWeight: '83.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 900, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6410B',
        manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-12-31T00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10002,
    sponsorShipmentID: 'PKG428-60B', freightForwarderScheduleID: '3919519202',
    plannedDispatchDate: '2026-05-26T06:50:38.673162', actualDispatchDate: null, dispatchDate: null,
    netWeight: '140 KG', temperature: 'Store 2 - 8 deg C', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
    statusID: 63, statusDescription: 'Shipment Scheduled',
    estimatedArrivalDate: '2026-06-06T06:50:38.673162', vendorShipmentID: 'VSI-60B',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-25T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-26T09:15:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550281600',
        fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
        readyForDispatchQuantity: 1500, uomQA: 'EA', noOfPallets: '15',
        netWeight: '112 KG', grossWeight: '140.000', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
        dispatchQuantity: 1500, dispatchUOM: 'EA', vendorBatchNumber: 'OPD6411B',
        manufacturingDate: '2026-02-10T00:00', expiryDate: '2028-01-31T00:00',
      }),
    ],
  }),

  // 64 — Shipped (showGenerateDocumentAction: true → "Generate Proforma" button shown)
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10003,
    sponsorShipmentID: 'PKG428-61A', freightForwarderScheduleID: '3919519203',
    plannedDispatchDate: '2026-05-20T06:50:38.673162', actualDispatchDate: '2026-05-20T06:50:38.673162', dispatchDate: '2026-05-20T06:50:38.673162',
    netWeight: '85 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 64, statusDescription: 'Shipped', showGenerateDocumentAction: true,
    estimatedArrivalDate: '2026-05-30T06:50:38.673162', vendorShipmentID: 'VSI-61A',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-18T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-19T09:00:00' },
      { shipmentStatus: 'Shipped',             lastUpdatedDate: '2026-05-20T11:45:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550281700',
        fgMaterialCode: '5520011', fgMaterialName: 'REYATAZ CAP 300MG (60CT)',
        readyForDispatchQuantity: 1000, uomQA: 'EA', noOfPallets: '10',
        netWeight: '72 KG', grossWeight: '85.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1000, dispatchUOM: 'EA', vendorBatchNumber: 'REY6412C',
        manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-11-30T00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10004,
    sponsorShipmentID: 'PKG428-61B', freightForwarderScheduleID: '3919519204',
    plannedDispatchDate: '2026-05-21T06:50:38.673162', actualDispatchDate: '2026-05-21T06:50:38.673162', dispatchDate: '2026-05-21T06:50:38.673162',
    netWeight: '160 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 64, statusDescription: 'Shipped', showGenerateDocumentAction: true,
    estimatedArrivalDate: '2026-06-01T06:50:38.673162', vendorShipmentID: 'VSI-61B',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-19T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-20T10:00:00' },
      { shipmentStatus: 'Shipped',             lastUpdatedDate: '2026-05-21T12:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550281800',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 1750, uomQA: 'EA', noOfPallets: '18',
        netWeight: '138 KG', grossWeight: '160.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1750, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6413D',
        manufacturingDate: '2026-01-25T00:00', expiryDate: '2027-12-31T00:00',
      }),
    ],
  }),

  // 65 — Proforma Requested
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10005,
    sponsorShipmentID: 'PKG428-62A', freightForwarderScheduleID: '3919519205',
    plannedDispatchDate: '2026-05-18T06:50:38.673162', actualDispatchDate: '2026-05-18T06:50:38.673162', dispatchDate: '2026-05-18T06:50:38.673162',
    netWeight: '160 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 65, statusDescription: 'Proforma Requested',
    estimatedArrivalDate: '2026-05-28T06:50:38.673162', vendorShipmentID: 'VSI-62A',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-16T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-17T09:00:00' },
      { shipmentStatus: 'Shipped',             lastUpdatedDate: '2026-05-18T11:00:00' },
      { shipmentStatus: 'Proforma Requested',  lastUpdatedDate: '2026-05-19T14:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550281900',
        fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
        readyForDispatchQuantity: 1600, uomQA: 'TS', noOfPallets: '16',
        netWeight: '128 KG', grossWeight: '160.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1600, dispatchUOM: 'TS', vendorBatchNumber: 'ENTC6414E',
        manufacturingDate: '2026-02-01T00:00', expiryDate: '2027-10-31T00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10006,
    sponsorShipmentID: 'PKG428-62B', freightForwarderScheduleID: '3919519206',
    plannedDispatchDate: '2026-05-19T06:50:38.673162', actualDispatchDate: '2026-05-19T06:50:38.673162', dispatchDate: '2026-05-19T06:50:38.673162',
    netWeight: '95 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 65, statusDescription: 'Proforma Requested',
    estimatedArrivalDate: '2026-05-29T06:50:38.673162', vendorShipmentID: 'VSI-62B',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-17T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-18T09:30:00' },
      { shipmentStatus: 'Shipped',             lastUpdatedDate: '2026-05-19T11:30:00' },
      { shipmentStatus: 'Proforma Requested',  lastUpdatedDate: '2026-05-20T13:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282000',
        fgMaterialCode: '7820048', fgMaterialName: 'ELIQUIS TAB 5MG (60CT)',
        readyForDispatchQuantity: 1300, uomQA: 'EA', noOfPallets: '13',
        netWeight: '78 KG', grossWeight: '95.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1300, dispatchUOM: 'EA', vendorBatchNumber: 'ELI6415F',
        manufacturingDate: '2026-02-15T00:00', expiryDate: '2028-02-28T00:00',
      }),
    ],
  }),

  // 66 — Proforma Uploaded
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10007,
    sponsorShipmentID: 'PKG428-63A', freightForwarderScheduleID: '3919519207',
    plannedDispatchDate: '2026-05-17T06:50:38.673162', actualDispatchDate: '2026-05-17T06:50:38.673162', dispatchDate: '2026-05-17T06:50:38.673162',
    netWeight: '88 KG', temperature: 'Store 2 - 8 deg C', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
    statusID: 66, statusDescription: 'Proforma Uploaded',
    estimatedArrivalDate: '2026-05-27T06:50:38.673162', vendorShipmentID: 'VSI-63A',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-15T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-16T09:00:00' },
      { shipmentStatus: 'Shipped',             lastUpdatedDate: '2026-05-17T11:00:00' },
      { shipmentStatus: 'Proforma Requested',  lastUpdatedDate: '2026-05-18T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',   lastUpdatedDate: '2026-05-19T15:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282100',
        fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
        readyForDispatchQuantity: 950, uomQA: 'EA', noOfPallets: '10',
        netWeight: '72 KG', grossWeight: '88.000', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
        dispatchQuantity: 950, dispatchUOM: 'EA', vendorBatchNumber: 'OPD6416G',
        manufacturingDate: '2026-02-20T00:00', expiryDate: '2028-01-31T00:00',
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282101',
        fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
        readyForDispatchQuantity: 950, uomQA: 'EA', noOfPallets: '10',
        netWeight: '72 KG', grossWeight: '88.000', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
        dispatchQuantity: 950, dispatchUOM: 'EA', vendorBatchNumber: 'OPD6416G',
        manufacturingDate: '2026-02-20T00:00', expiryDate: '2028-01-31T00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10008,
    sponsorShipmentID: 'PKG428-63B', freightForwarderScheduleID: '3919519208',
    plannedDispatchDate: '2026-05-16T06:50:38.673162', actualDispatchDate: '2026-05-16T06:50:38.673162', dispatchDate: '2026-05-16T06:50:38.673162',
    netWeight: '100 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 66, statusDescription: 'Proforma Uploaded',
    estimatedArrivalDate: '2026-05-26T06:50:38.673162', vendorShipmentID: 'VSI-63B',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized', lastUpdatedDate: '2026-05-14T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',  lastUpdatedDate: '2026-05-15T09:00:00' },
      { shipmentStatus: 'Shipped',             lastUpdatedDate: '2026-05-16T11:00:00' },
      { shipmentStatus: 'Proforma Requested',  lastUpdatedDate: '2026-05-17T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',   lastUpdatedDate: '2026-05-18T15:30:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282200',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 1100, uomQA: 'EA', noOfPallets: '11',
        netWeight: '85 KG', grossWeight: '100.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1100, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6417H',
        manufacturingDate: '2026-01-28T00:00', expiryDate: '2027-12-31T00:00',
      }),
    ],
  }),

  // 67 — Proforma Digitalized
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10009,
    sponsorShipmentID: 'PKG428-64A', freightForwarderScheduleID: '3919519209',
    plannedDispatchDate: '2026-05-15T06:50:38.673162', actualDispatchDate: '2026-05-15T06:50:38.673162', dispatchDate: '2026-05-15T06:50:38.673162',
    netWeight: '180 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 67, statusDescription: 'Proforma Digitalized',
    estimatedArrivalDate: '2026-05-25T06:50:38.673162', vendorShipmentID: 'VSI-64A',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',   lastUpdatedDate: '2026-05-13T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',    lastUpdatedDate: '2026-05-14T09:00:00' },
      { shipmentStatus: 'Shipped',               lastUpdatedDate: '2026-05-15T11:00:00' },
      { shipmentStatus: 'Proforma Requested',    lastUpdatedDate: '2026-05-16T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',     lastUpdatedDate: '2026-05-17T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',  lastUpdatedDate: '2026-05-18T10:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282300',
        fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
        readyForDispatchQuantity: 2000, uomQA: 'TS', noOfPallets: '20',
        netWeight: '150 KG', grossWeight: '180.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 2000, dispatchUOM: 'TS', vendorBatchNumber: 'ENTC6418I',
        manufacturingDate: '2026-01-10T00:00', expiryDate: '2027-10-31T00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10010,
    sponsorShipmentID: 'PKG428-64B', freightForwarderScheduleID: '3919519210',
    plannedDispatchDate: '2026-05-14T06:50:38.673162', actualDispatchDate: '2026-05-14T06:50:38.673162', dispatchDate: '2026-05-14T06:50:38.673162',
    netWeight: '75 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 67, statusDescription: 'Proforma Digitalized',
    estimatedArrivalDate: '2026-05-24T06:50:38.673162', vendorShipmentID: 'VSI-64B',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',   lastUpdatedDate: '2026-05-12T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',    lastUpdatedDate: '2026-05-13T09:00:00' },
      { shipmentStatus: 'Shipped',               lastUpdatedDate: '2026-05-14T11:00:00' },
      { shipmentStatus: 'Proforma Requested',    lastUpdatedDate: '2026-05-15T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',     lastUpdatedDate: '2026-05-16T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',  lastUpdatedDate: '2026-05-17T10:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282400',
        fgMaterialCode: '6610029', fgMaterialName: 'SUSTIVA CAP 200MG (90CT)',
        readyForDispatchQuantity: 850, uomQA: 'EA', noOfPallets: '9',
        netWeight: '62 KG', grossWeight: '75.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 850, dispatchUOM: 'EA', vendorBatchNumber: 'SUS6419J',
        manufacturingDate: '2026-02-05T00:00', expiryDate: '2028-03-31T00:00',
      }),
    ],
  }),

  // 68 — Proforma Sent
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10011,
    sponsorShipmentID: 'PKG428-65A', freightForwarderScheduleID: '3919519211',
    plannedDispatchDate: '2026-05-12T06:50:38.673162', actualDispatchDate: '2026-05-12T06:50:38.673162', dispatchDate: '2026-05-12T06:50:38.673162',
    netWeight: '235 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 68, statusDescription: 'Proforma Sent',
    estimatedArrivalDate: '2026-05-24T06:50:38.673162', vendorShipmentID: 'VSI-65A',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',   lastUpdatedDate: '2026-05-10T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',    lastUpdatedDate: '2026-05-11T09:00:00' },
      { shipmentStatus: 'Shipped',               lastUpdatedDate: '2026-05-12T11:00:00' },
      { shipmentStatus: 'Proforma Requested',    lastUpdatedDate: '2026-05-13T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',     lastUpdatedDate: '2026-05-14T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',  lastUpdatedDate: '2026-05-15T10:00:00' },
      { shipmentStatus: 'Proforma Sent',         lastUpdatedDate: '2026-05-16T11:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282500',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 2600, uomQA: 'EA', noOfPallets: '26',
        netWeight: '200 KG', grossWeight: '235.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 2600, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6420K',
        manufacturingDate: '2026-01-05T00:00', expiryDate: '2027-12-31T00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10012,
    sponsorShipmentID: 'PKG428-65B', freightForwarderScheduleID: '3919519212',
    plannedDispatchDate: '2026-05-13T06:50:38.673162', actualDispatchDate: '2026-05-13T06:50:38.673162', dispatchDate: '2026-05-13T06:50:38.673162',
    netWeight: '110 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 68, statusDescription: 'Proforma Sent',
    estimatedArrivalDate: '2026-05-25T06:50:38.673162', vendorShipmentID: 'VSI-65B',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',   lastUpdatedDate: '2026-05-11T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',    lastUpdatedDate: '2026-05-12T09:00:00' },
      { shipmentStatus: 'Shipped',               lastUpdatedDate: '2026-05-13T11:00:00' },
      { shipmentStatus: 'Proforma Requested',    lastUpdatedDate: '2026-05-14T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',     lastUpdatedDate: '2026-05-15T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',  lastUpdatedDate: '2026-05-16T10:00:00' },
      { shipmentStatus: 'Proforma Sent',         lastUpdatedDate: '2026-05-17T11:30:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282600',
        fgMaterialCode: '7820048', fgMaterialName: 'ELIQUIS TAB 5MG (60CT)',
        readyForDispatchQuantity: 1450, uomQA: 'EA', noOfPallets: '15',
        netWeight: '92 KG', grossWeight: '110.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1450, dispatchUOM: 'EA', vendorBatchNumber: 'ELI6421L',
        manufacturingDate: '2026-03-01T00:00', expiryDate: '2028-02-28T00:00',
      }),
    ],
  }),

  // 59 — Goods Received (GRN not yet generated)
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10013,
    sponsorShipmentID: 'PKG428-66A', freightForwarderScheduleID: '3919519213',
    plannedDispatchDate: '2026-05-08T06:50:38.673162', actualDispatchDate: '2026-05-08T06:50:38.673162', dispatchDate: '2026-05-08T06:50:38.673162',
    netWeight: '155 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-20T06:50:38.673162', vendorShipmentID: 'VSI-66A',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',   lastUpdatedDate: '2026-05-06T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',    lastUpdatedDate: '2026-05-07T09:00:00' },
      { shipmentStatus: 'Shipped',               lastUpdatedDate: '2026-05-08T11:00:00' },
      { shipmentStatus: 'Proforma Requested',    lastUpdatedDate: '2026-05-09T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',     lastUpdatedDate: '2026-05-10T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',  lastUpdatedDate: '2026-05-11T10:00:00' },
      { shipmentStatus: 'Proforma Sent',         lastUpdatedDate: '2026-05-12T11:00:00' },
      { shipmentStatus: 'Goods Received',        lastUpdatedDate: '2026-05-20T14:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282700',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 1200, uomQA: 'EA', noOfPallets: '12',
        netWeight: '95 KG', grossWeight: '112.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1200, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6422M',
        manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-12-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282701',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (9BLP X 10) FR',
        readyForDispatchQuantity: 500, uomQA: 'EA', noOfPallets: '5',
        netWeight: '40 KG', grossWeight: '43.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 500, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6422N',
        manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-12-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10014,
    sponsorShipmentID: 'PKG428-66B', freightForwarderScheduleID: '3919519214',
    plannedDispatchDate: '2026-05-09T06:50:38.673162', actualDispatchDate: '2026-05-09T06:50:38.673162', dispatchDate: '2026-05-09T06:50:38.673162',
    netWeight: '120 KG', temperature: 'Store 2 - 8 deg C', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-19T06:50:38.673162', vendorShipmentID: 'VSI-66B',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',   lastUpdatedDate: '2026-05-07T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',    lastUpdatedDate: '2026-05-08T09:00:00' },
      { shipmentStatus: 'Shipped',               lastUpdatedDate: '2026-05-09T11:00:00' },
      { shipmentStatus: 'Proforma Requested',    lastUpdatedDate: '2026-05-10T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',     lastUpdatedDate: '2026-05-11T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',  lastUpdatedDate: '2026-05-12T10:00:00' },
      { shipmentStatus: 'Proforma Sent',         lastUpdatedDate: '2026-05-13T11:00:00' },
      { shipmentStatus: 'Goods Received',        lastUpdatedDate: '2026-05-19T16:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282800',
        fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
        readyForDispatchQuantity: 1050, uomQA: 'EA', noOfPallets: '11',
        netWeight: '100 KG', grossWeight: '120.000', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
        dispatchQuantity: 1050, dispatchUOM: 'EA', vendorBatchNumber: 'OPD6423N',
        manufacturingDate: '2026-02-10T00:00', expiryDate: '2028-01-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),

  // 69 — Consignee GRN Generated
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10015,
    sponsorShipmentID: 'PKG428-67A', freightForwarderScheduleID: '3919519215',
    plannedDispatchDate: '2026-05-03T06:50:38.673162', actualDispatchDate: '2026-05-03T06:50:38.673162', dispatchDate: '2026-05-03T06:50:38.673162',
    netWeight: '180 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-13T06:50:38.673162', vendorShipmentID: 'VSI-67A',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',      lastUpdatedDate: '2026-05-01T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-05-02T09:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-05-03T11:00:00' },
      { shipmentStatus: 'Proforma Requested',       lastUpdatedDate: '2026-05-04T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',        lastUpdatedDate: '2026-05-05T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',     lastUpdatedDate: '2026-05-06T10:00:00' },
      { shipmentStatus: 'Proforma Sent',            lastUpdatedDate: '2026-05-07T11:00:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-13T14:00:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-14T10:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550282900',
        fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
        readyForDispatchQuantity: 1800, uomQA: 'TS', noOfPallets: '18',
        netWeight: '148 KG', grossWeight: '180.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1800, dispatchUOM: 'TS', vendorBatchNumber: 'ENTC6424O',
        manufacturingDate: '2026-01-10T00:00', expiryDate: '2027-10-31T00:00',
        consigneeGRNNumber: 'GRN-2026-007', consigneeGRNGeneratedDate: '2026-05-14T10:00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 10016,
    sponsorShipmentID: 'PKG428-67B', freightForwarderScheduleID: '3919519216',
    plannedDispatchDate: '2026-05-04T06:50:38.673162', actualDispatchDate: '2026-05-04T06:50:38.673162', dispatchDate: '2026-05-04T06:50:38.673162',
    netWeight: '95 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-14T06:50:38.673162', vendorShipmentID: 'VSI-67B',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Authorized',      lastUpdatedDate: '2026-05-02T08:00:00' },
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-05-03T09:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-05-04T11:00:00' },
      { shipmentStatus: 'Proforma Requested',       lastUpdatedDate: '2026-05-05T13:00:00' },
      { shipmentStatus: 'Proforma Uploaded',        lastUpdatedDate: '2026-05-06T15:00:00' },
      { shipmentStatus: 'Proforma Digitalized',     lastUpdatedDate: '2026-05-07T10:00:00' },
      { shipmentStatus: 'Proforma Sent',            lastUpdatedDate: '2026-05-08T11:30:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-14T14:00:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-15T09:30:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283000',
        fgMaterialCode: '5520011', fgMaterialName: 'REYATAZ CAP 300MG (60CT)',
        readyForDispatchQuantity: 900, uomQA: 'EA', noOfPallets: '9',
        netWeight: '80 KG', grossWeight: '95.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 900, dispatchUOM: 'EA', vendorBatchNumber: 'REY6425P',
        manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-11-30T00:00',
        consigneeGRNNumber: 'GRN-2026-008', consigneeGRNGeneratedDate: '2026-05-15T09:30:00',
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283001',
        fgMaterialCode: '5520011', fgMaterialName: 'REYATAZ CAP 300MG (60CT)',
        readyForDispatchQuantity: 400, uomQA: 'EA', noOfPallets: '4',
        netWeight: '35 KG', grossWeight: '40.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 400, dispatchUOM: 'EA', vendorBatchNumber: 'REY6425Q',
        manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-11-30T00:00',
        consigneeGRNNumber: 'GRN-2026-008', consigneeGRNGeneratedDate: '2026-05-15T09:30:00',
      }),
    ],
  }),
];

// ─── Post-Delivery shipments (fetchShipmentDataForLogistics, view=postDelivery) ─
const POST_DELIVERY_SHIPMENTS = [
  // 64 — Shipped: demoable "Sync Status" row (upload FF XML → Goods Received)
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20000,
    sponsorShipmentID: 'PKG428-25F', freightForwarderScheduleID: '3919519200',
    plannedDispatchDate: '2026-05-03T06:50:38.673162', actualDispatchDate: '2026-05-03T06:50:38.673162', dispatchDate: '2026-05-03T06:50:38.673162',
    netWeight: '1574 KG', temperature: 'Ambient Coldstore 2-8', tempRangeAndSetPoint: 'Ambient Coldstore 2-8',
    statusID: 64, statusDescription: 'Shipped',
    estimatedArrivalDate: '2026-05-10T06:50:38.673162', vendorShipmentID: 'VSI-25F',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled', lastUpdatedDate: '2026-05-01T08:20:20' },
      { shipmentStatus: 'Shipped',            lastUpdatedDate: '2026-05-03T16:40:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283100',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 900, uomQA: 'EA', noOfPallets: '23',
        netWeight: '124 KG', grossWeight: '149.000', tempRangeAndSetPoint: 'Ambient Coldstore 2-8',
        dispatchQuantity: 900, dispatchUOM: 'EA', vendorBatchNumber: 'C24051',
        manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-12-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),
  // 59 — Goods Received (GRN not yet generated)
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20001,
    sponsorShipmentID: 'PKG428-70A', freightForwarderScheduleID: '3919519220',
    plannedDispatchDate: '2026-05-05T06:50:38.673162', actualDispatchDate: '2026-05-06T06:50:38.673162', dispatchDate: '2026-05-06T06:50:38.673162',
    netWeight: '200 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-18T06:50:38.673162', vendorShipmentID: 'VSI-70A',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled', lastUpdatedDate: '2026-05-05T09:00:00' },
      { shipmentStatus: 'Shipped',            lastUpdatedDate: '2026-05-06T11:00:00' },
      { shipmentStatus: 'Goods Received',     lastUpdatedDate: '2026-05-18T14:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283000',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 800, uomQA: 'EA', noOfPallets: '8',
        netWeight: '120 KG', grossWeight: '133.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 800, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6430A',
        manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-12-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283001',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (9BLP X 10) FR',
        readyForDispatchQuantity: 400, uomQA: 'EA', noOfPallets: '4',
        netWeight: '60 KG', grossWeight: '67.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 400, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6430B',
        manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-12-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20002,
    sponsorShipmentID: 'PKG428-70B', freightForwarderScheduleID: '3919519221',
    plannedDispatchDate: '2026-05-03T06:50:38.673162', actualDispatchDate: '2026-05-03T06:50:38.673162', dispatchDate: '2026-05-03T06:50:38.673162',
    netWeight: '130 KG', temperature: 'Store 2 - 8 deg C', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-10T06:50:38.673162', vendorShipmentID: 'VSI-70B',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled', lastUpdatedDate: '2026-05-03T08:00:00' },
      { shipmentStatus: 'Shipped',            lastUpdatedDate: '2026-05-03T12:00:00' },
      { shipmentStatus: 'Goods Received',     lastUpdatedDate: '2026-05-10T16:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283100',
        fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
        readyForDispatchQuantity: 900, uomQA: 'EA', noOfPallets: '9',
        netWeight: '110 KG', grossWeight: '130.000', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
        dispatchQuantity: 900, dispatchUOM: 'EA', vendorBatchNumber: 'OPD6431B',
        manufacturingDate: '2026-02-10T00:00', expiryDate: '2028-01-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20003,
    sponsorShipmentID: 'PKG428-70C', freightForwarderScheduleID: '3919519222',
    plannedDispatchDate: '2026-05-04T06:50:38.673162', actualDispatchDate: '2026-05-05T06:50:38.673162', dispatchDate: '2026-05-05T06:50:38.673162',
    netWeight: '175 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-12T06:50:38.673162', vendorShipmentID: 'VSI-70C',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled', lastUpdatedDate: '2026-05-04T09:00:00' },
      { shipmentStatus: 'Shipped',            lastUpdatedDate: '2026-05-05T11:00:00' },
      { shipmentStatus: 'Goods Received',     lastUpdatedDate: '2026-05-12T15:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283200',
        fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
        readyForDispatchQuantity: 900, uomQA: 'TS', noOfPallets: '9',
        netWeight: '100 KG', grossWeight: '111.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 900, dispatchUOM: 'TS', vendorBatchNumber: 'ENTC6432C',
        manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-10-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283201',
        fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
        readyForDispatchQuantity: 500, uomQA: 'TS', noOfPallets: '5',
        netWeight: '60 KG', grossWeight: '64.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 500, dispatchUOM: 'TS', vendorBatchNumber: 'ENTC6432D',
        manufacturingDate: '2026-01-25T00:00', expiryDate: '2027-10-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20004,
    sponsorShipmentID: 'PKG428-70D', freightForwarderScheduleID: '3919519223',
    plannedDispatchDate: '2026-05-02T06:50:38.673162', actualDispatchDate: '2026-05-02T06:50:38.673162', dispatchDate: '2026-05-02T06:50:38.673162',
    netWeight: '85 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-09T06:50:38.673162', vendorShipmentID: 'VSI-70D',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled', lastUpdatedDate: '2026-05-02T08:00:00' },
      { shipmentStatus: 'Shipped',            lastUpdatedDate: '2026-05-02T12:00:00' },
      { shipmentStatus: 'Goods Received',     lastUpdatedDate: '2026-05-09T14:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283300',
        fgMaterialCode: '5520011', fgMaterialName: 'REYATAZ CAP 300MG (60CT)',
        readyForDispatchQuantity: 750, uomQA: 'EA', noOfPallets: '8',
        netWeight: '70 KG', grossWeight: '85.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 750, dispatchUOM: 'EA', vendorBatchNumber: 'REY6433D',
        manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-11-30T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20005,
    sponsorShipmentID: 'PKG428-70E', freightForwarderScheduleID: '3919519224',
    plannedDispatchDate: '2026-05-06T06:50:38.673162', actualDispatchDate: '2026-05-07T06:50:38.673162', dispatchDate: '2026-05-07T06:50:38.673162',
    netWeight: '105 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-14T06:50:38.673162', vendorShipmentID: 'VSI-70E',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled', lastUpdatedDate: '2026-05-06T09:00:00' },
      { shipmentStatus: 'Shipped',            lastUpdatedDate: '2026-05-07T11:30:00' },
      { shipmentStatus: 'Goods Received',     lastUpdatedDate: '2026-05-14T16:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283400',
        fgMaterialCode: '6610029', fgMaterialName: 'SUSTIVA CAP 200MG (90CT)',
        readyForDispatchQuantity: 600, uomQA: 'EA', noOfPallets: '6',
        netWeight: '54 KG', grossWeight: '63.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 600, dispatchUOM: 'EA', vendorBatchNumber: 'SUS6434E',
        manufacturingDate: '2026-02-05T00:00', expiryDate: '2028-03-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283401',
        fgMaterialCode: '6610029', fgMaterialName: 'SUSTIVA CAP 200MG (90CT)',
        readyForDispatchQuantity: 400, uomQA: 'EA', noOfPallets: '4',
        netWeight: '36 KG', grossWeight: '42.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 400, dispatchUOM: 'EA', vendorBatchNumber: 'SUS6434F',
        manufacturingDate: '2026-02-10T00:00', expiryDate: '2028-03-31T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20006,
    sponsorShipmentID: 'PKG428-70F', freightForwarderScheduleID: '3919519225',
    plannedDispatchDate: '2026-05-08T06:50:38.673162', actualDispatchDate: '2026-05-08T06:50:38.673162', dispatchDate: '2026-05-08T06:50:38.673162',
    netWeight: '170 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 59, statusDescription: 'Goods Received',
    estimatedArrivalDate: '2026-05-16T06:50:38.673162', vendorShipmentID: 'VSI-70F',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled', lastUpdatedDate: '2026-05-08T08:00:00' },
      { shipmentStatus: 'Shipped',            lastUpdatedDate: '2026-05-08T12:00:00' },
      { shipmentStatus: 'Goods Received',     lastUpdatedDate: '2026-05-16T14:30:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283500',
        fgMaterialCode: '7820048', fgMaterialName: 'ELIQUIS TAB 5MG (60CT)',
        readyForDispatchQuantity: 1200, uomQA: 'EA', noOfPallets: '12',
        netWeight: '100 KG', grossWeight: '110.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1200, dispatchUOM: 'EA', vendorBatchNumber: 'ELI6435F',
        manufacturingDate: '2026-03-01T00:00', expiryDate: '2028-02-28T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550283501',
        fgMaterialCode: '7820048', fgMaterialName: 'ELIQUIS TAB 5MG (60CT)',
        readyForDispatchQuantity: 650, uomQA: 'EA', noOfPallets: '7',
        netWeight: '55 KG', grossWeight: '60.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 650, dispatchUOM: 'EA', vendorBatchNumber: 'ELI6435G',
        manufacturingDate: '2026-03-05T00:00', expiryDate: '2028-02-28T00:00',
        consigneeGRNNumber: null, consigneeGRNGeneratedDate: null,
      }),
    ],
  }),

  // 69 — Consignee GRN Generated
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20007,
    sponsorShipmentID: 'PKG428-71A', freightForwarderScheduleID: '3919519230',
    plannedDispatchDate: '2026-04-25T06:50:38.673162', actualDispatchDate: '2026-04-25T06:50:38.673162', dispatchDate: '2026-04-25T06:50:38.673162',
    netWeight: '140 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-03T06:50:38.673162', vendorShipmentID: 'VSI-71A',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-04-25T09:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-04-25T12:00:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-03T14:00:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-04T10:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284000',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 800, uomQA: 'EA', noOfPallets: '8',
        netWeight: '74 KG', grossWeight: '87.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 800, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6440G',
        manufacturingDate: '2026-01-05T00:00', expiryDate: '2027-12-31T00:00',
        consigneeGRNNumber: 'GRN-2026-001', consigneeGRNGeneratedDate: '2026-05-04T10:00:00',
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284001',
        fgMaterialCode: '2451', fgMaterialName: 'BARACLUDE TAB 0.5MG (3BLP X 10) FR',
        readyForDispatchQuantity: 500, uomQA: 'EA', noOfPallets: '5',
        netWeight: '46 KG', grossWeight: '53.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 500, dispatchUOM: 'EA', vendorBatchNumber: 'PQR6440H',
        manufacturingDate: '2026-01-10T00:00', expiryDate: '2027-12-31T00:00',
        consigneeGRNNumber: 'GRN-2026-001', consigneeGRNGeneratedDate: '2026-05-04T10:00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20008,
    sponsorShipmentID: 'PKG428-71B', freightForwarderScheduleID: '3919519231',
    plannedDispatchDate: '2026-04-26T06:50:38.673162', actualDispatchDate: '2026-04-26T06:50:38.673162', dispatchDate: '2026-04-26T06:50:38.673162',
    netWeight: '78 KG', temperature: 'Store 2 - 8 deg C', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-04T06:50:38.673162', vendorShipmentID: 'VSI-71B',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-04-26T08:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-04-26T12:00:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-04T14:00:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-05T09:30:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284100',
        fgMaterialCode: '1374351', fgMaterialName: 'FG OPDIVO SINJ 240MG (1VLX24ML) FR (SerI)',
        readyForDispatchQuantity: 700, uomQA: 'EA', noOfPallets: '7',
        netWeight: '65 KG', grossWeight: '78.000', tempRangeAndSetPoint: 'Store 2 - 8 deg C',
        dispatchQuantity: 700, dispatchUOM: 'EA', vendorBatchNumber: 'OPD6441H',
        manufacturingDate: '2026-02-10T00:00', expiryDate: '2028-01-31T00:00',
        consigneeGRNNumber: 'GRN-2026-002', consigneeGRNGeneratedDate: '2026-05-05T09:30:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20009,
    sponsorShipmentID: 'PKG428-71C', freightForwarderScheduleID: '3919519232',
    plannedDispatchDate: '2026-04-27T06:50:38.673162', actualDispatchDate: '2026-04-28T06:50:38.673162', dispatchDate: '2026-04-28T06:50:38.673162',
    netWeight: '140 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-06T06:50:38.673162', vendorShipmentID: 'VSI-71C',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-04-27T09:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-04-28T11:00:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-06T14:00:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-07T10:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284200',
        fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
        readyForDispatchQuantity: 700, uomQA: 'TS', noOfPallets: '7',
        netWeight: '80 KG', grossWeight: '90.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 700, dispatchUOM: 'TS', vendorBatchNumber: 'ENTC6442I',
        manufacturingDate: '2026-01-20T00:00', expiryDate: '2027-10-31T00:00',
        consigneeGRNNumber: 'GRN-2026-003', consigneeGRNGeneratedDate: '2026-05-07T10:00:00',
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284201',
        fgMaterialCode: '2340', fgMaterialName: 'Entecavir Tab 0.5 mg Bulk Drum PATH(SFG)',
        readyForDispatchQuantity: 400, uomQA: 'TS', noOfPallets: '4',
        netWeight: '45 KG', grossWeight: '50.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 400, dispatchUOM: 'TS', vendorBatchNumber: 'ENTC6442J',
        manufacturingDate: '2026-01-25T00:00', expiryDate: '2027-10-31T00:00',
        consigneeGRNNumber: 'GRN-2026-003', consigneeGRNGeneratedDate: '2026-05-07T10:00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20010,
    sponsorShipmentID: 'PKG428-71D', freightForwarderScheduleID: '3919519233',
    plannedDispatchDate: '2026-04-29T06:50:38.673162', actualDispatchDate: '2026-04-29T06:50:38.673162', dispatchDate: '2026-04-29T06:50:38.673162',
    netWeight: '95 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-07T06:50:38.673162', vendorShipmentID: 'VSI-71D',
    vendorName: 'Oxford Biomedica',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-04-29T09:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-04-29T12:00:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-07T14:00:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-08T11:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284300',
        fgMaterialCode: '5520011', fgMaterialName: 'REYATAZ CAP 300MG (60CT)',
        readyForDispatchQuantity: 950, uomQA: 'EA', noOfPallets: '10',
        netWeight: '85 KG', grossWeight: '95.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 950, dispatchUOM: 'EA', vendorBatchNumber: 'REY6443J',
        manufacturingDate: '2026-01-15T00:00', expiryDate: '2027-11-30T00:00',
        consigneeGRNNumber: 'GRN-2026-004', consigneeGRNGeneratedDate: '2026-05-08T11:00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20011,
    sponsorShipmentID: 'PKG428-71E', freightForwarderScheduleID: '3919519234',
    plannedDispatchDate: '2026-04-30T06:50:38.673162', actualDispatchDate: '2026-04-30T06:50:38.673162', dispatchDate: '2026-04-30T06:50:38.673162',
    netWeight: '160 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-08T06:50:38.673162', vendorShipmentID: 'VSI-71E',
    vendorName: 'Cipla - Goa',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-04-30T08:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-04-30T12:00:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-08T15:00:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-09T10:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284400',
        fgMaterialCode: '7820048', fgMaterialName: 'ELIQUIS TAB 5MG (60CT)',
        readyForDispatchQuantity: 1000, uomQA: 'EA', noOfPallets: '10',
        netWeight: '85 KG', grossWeight: '97.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 1000, dispatchUOM: 'EA', vendorBatchNumber: 'ELI6444K',
        manufacturingDate: '2026-03-01T00:00', expiryDate: '2028-02-28T00:00',
        consigneeGRNNumber: 'GRN-2026-005', consigneeGRNGeneratedDate: '2026-05-09T10:00:00',
      }),
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284401',
        fgMaterialCode: '7820048', fgMaterialName: 'ELIQUIS TAB 5MG (60CT)',
        readyForDispatchQuantity: 650, uomQA: 'EA', noOfPallets: '7',
        netWeight: '55 KG', grossWeight: '63.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 650, dispatchUOM: 'EA', vendorBatchNumber: 'ELI6444L',
        manufacturingDate: '2026-03-05T00:00', expiryDate: '2028-02-28T00:00',
        consigneeGRNNumber: 'GRN-2026-005', consigneeGRNGeneratedDate: '2026-05-09T10:00:00',
      }),
    ],
  }),
  makeShipmentRow({
    collabrixFGBatchShipmentID: 20012,
    sponsorShipmentID: 'PKG428-71F', freightForwarderScheduleID: '3919519235',
    plannedDispatchDate: '2026-05-01T06:50:38.673162', actualDispatchDate: '2026-05-01T06:50:38.673162', dispatchDate: '2026-05-01T06:50:38.673162',
    netWeight: '82 KG', temperature: 'Ambient', tempRangeAndSetPoint: 'Ambient',
    statusID: 69, statusDescription: 'Consignee GRN Generated',
    estimatedArrivalDate: '2026-05-09T06:50:38.673162', vendorShipmentID: 'VSI-71F',
    vendorName: 'Sun Pharma',
    shipmentEvents: [
      { shipmentStatus: 'Shipment Scheduled',       lastUpdatedDate: '2026-05-01T08:00:00' },
      { shipmentStatus: 'Shipped',                  lastUpdatedDate: '2026-05-01T12:00:00' },
      { shipmentStatus: 'Goods Received',           lastUpdatedDate: '2026-05-09T14:30:00' },
      { shipmentStatus: 'Consignee GRN Generated',  lastUpdatedDate: '2026-05-10T09:00:00' },
    ],
    batchesList: [
      makeShipmentBatch({
        scheduleAgreementNumber: '4550284500',
        fgMaterialCode: '6610029', fgMaterialName: 'SUSTIVA CAP 200MG (90CT)',
        readyForDispatchQuantity: 800, uomQA: 'EA', noOfPallets: '8',
        netWeight: '70 KG', grossWeight: '82.000', tempRangeAndSetPoint: 'Ambient',
        dispatchQuantity: 800, dispatchUOM: 'EA', vendorBatchNumber: 'SUS6445L',
        manufacturingDate: '2026-02-05T00:00', expiryDate: '2028-03-31T00:00',
        consigneeGRNNumber: 'GRN-2026-006', consigneeGRNGeneratedDate: '2026-05-10T09:00:00',
      }),
    ],
  }),
];

// ─── Exports ──────────────────────────────────────────────────────────────────

export const MOCK_PRE_SCHEDULE_RESPONSE = {
  data: {
    batchesList:      PRE_SCHEDULE_ROWS,
    totalNoOfRecords: PRE_SCHEDULE_ROWS.length,
    numberOfPages:    1,
  },
};

// backward-compat alias used by older imports
export const MOCK_FETCH_RESPONSE = MOCK_PRE_SCHEDULE_RESPONSE;

// Split all authored shipment rows across the two views by status — mirrors the Corda
// buildSelectedDashboardViewClause. Post-Schedule View = Shipment Scheduled (63) only;
// Post-Shipment View = Shipped (64) + Proforma (65-68) + Goods Received (59) +
// Consignee GRN Generated (69). (Previously POST_SCHEDULE_SHIPMENTS also carried 64-69.)
const POST_SCHEDULE_VIEW_STATUS_IDS = [63];
const POST_DELIVERY_VIEW_STATUS_IDS = [64, 65, 66, 67, 68, 59, 69];
const ALL_SHIPMENT_ROWS = [...POST_SCHEDULE_SHIPMENTS, ...POST_DELIVERY_SHIPMENTS];
const POST_SCHEDULE_VIEW_ROWS = ALL_SHIPMENT_ROWS.filter((r) => POST_SCHEDULE_VIEW_STATUS_IDS.includes(r.statusID));
const POST_DELIVERY_VIEW_ROWS = ALL_SHIPMENT_ROWS.filter((r) => POST_DELIVERY_VIEW_STATUS_IDS.includes(r.statusID));

export const MOCK_SHIPMENT_RESPONSE = {
  postSchedule: {
    data: {
      shipmentList:     POST_SCHEDULE_VIEW_ROWS,
      totalNoOfRecords: POST_SCHEDULE_VIEW_ROWS.length,
      noOfPages:        1,
    },
  },
  postDelivery: {
    data: {
      shipmentList:     POST_DELIVERY_VIEW_ROWS,
      totalNoOfRecords: POST_DELIVERY_VIEW_ROWS.length,
      noOfPages:        1,
    },
  },
};

export const MOCK_SHIPMENTS = {
  preSchedule:  PRE_SCHEDULE_ROWS,
  postShipment: POST_SCHEDULE_VIEW_ROWS,
  postDelivery: POST_DELIVERY_VIEW_ROWS,
};

// ─── Other mock fixtures ──────────────────────────────────────────────────────
export const MOCK_PROFORMA_ROW = {
  vendorName:                 'Oxford Biomedica UK',
  sponsorShipmentID:          'PKG428-25F',
  freightForwarderScheduleID: '3919519200',
  plannedDispatchDate:        '2026-05-03T00:00:00',
};

export const MOCK_PROFORMA_BATCHES = [
  { scheduleAgreementNumber: '45502815', fgMaterialCode: '1223159', fgMaterialName: 'BARACLUDE TAB 1MG (3BLPX10)', vendorBatchNumber: 'C24051', dispatchQuantity: 923,  batchUOM: 'KG', manufacturingDate: '2025-11-26T00:00:00', expiryDate: '2027-10-31T00:00:00' },
  { scheduleAgreementNumber: '45502814', fgMaterialCode: '1223159', fgMaterialName: 'BARACLUDE TAB 1MG (3BLPX10)', vendorBatchNumber: 'C24052', dispatchQuantity: 651,  batchUOM: 'KG', manufacturingDate: '2025-11-26T00:00:00', expiryDate: '2027-10-31T00:00:00' },
  { scheduleAgreementNumber: '45502813', fgMaterialCode: '1223159', fgMaterialName: 'BARACLUDE TAB 1MG (3BLPX10)', vendorBatchNumber: 'C24053', dispatchQuantity: 3000, batchUOM: 'KG', manufacturingDate: '2025-11-26T00:00:00', expiryDate: '2027-10-31T00:00:00' },
];

export const MOCK_SHIPMENT_MESSAGES = [
  { ts: '2026-05-15 09:12', actor: 'CMO – Cipla Goa',    event: 'Pre-Shipment Documents Uploaded', description: 'Packing List, CoA and Manufacturing Cert received via email.' },
  { ts: '2026-05-15 10:04', actor: 'Sponsor Ops',         event: 'Documents Digitised',             description: 'All 3 documents successfully digitised and validated.' },
  { ts: '2026-05-16 14:30', actor: 'Sponsor Ops',         event: 'Shipment Scheduled',              description: 'FF DHL acknowledged slot for 18-May pickup.' },
  { ts: '2026-05-18 11:15', actor: 'Freight Forwarder',   event: 'Pickup confirmed',                description: 'Goods picked up from Cipla Goa.' },
];

export const MOCK_SHIPMENT_CONTAINER_BATCHES = [
  { scheduleAgreementNumber: '45502815', vendorBatchNumber: 'C24051', fgMaterialCode: '1223159', fgMaterialName: 'BARACLUDE TAB 1MG (3BLPX10)', brand: 'Baraclude', noOfPallets: 23, deliveryAddresseeOrgName: null, temperature: 'Ambient', netWeight: 124, grossWeight: 149, producedQuantity: 923,  batchUOM: 'KG', freightForwarderScheduleID: '3919519200', dispatchQuantity: 923,  manufacturingDate: '2025-11-26T00:00:00', expiryDate: '2027-10-31T00:00:00' },
  { scheduleAgreementNumber: '45502814', vendorBatchNumber: 'C24052', fgMaterialCode: '1223159', fgMaterialName: 'BARACLUDE TAB 1MG (3BLPX10)', brand: 'Baraclude', noOfPallets: 31, deliveryAddresseeOrgName: null, temperature: 'Ambient', netWeight: 44,  grossWeight: 69,  producedQuantity: 651,  batchUOM: 'KG', freightForwarderScheduleID: '3919519200', dispatchQuantity: 651,  manufacturingDate: '2025-11-26T00:00:00', expiryDate: '2027-10-31T00:00:00' },
  { scheduleAgreementNumber: '45502813', vendorBatchNumber: 'C24053', fgMaterialCode: '1223159', fgMaterialName: 'BARACLUDE TAB 1MG (3BLPX10)', brand: 'Baraclude', noOfPallets: 19, deliveryAddresseeOrgName: null, temperature: 'Ambient', netWeight: 222, grossWeight: 297, producedQuantity: 3000, batchUOM: 'KG', freightForwarderScheduleID: '3919519200', dispatchQuantity: 3000, manufacturingDate: '2025-11-26T00:00:00', expiryDate: '2027-10-31T00:00:00' },
];

// ─── Shipment Schedule ID Form ────────────────────────────────────────────────
// Sourced from "5_Viral Vector_Logistics.xlsx" → "Shipment Schedule ID Form" tab.
// Content Details (the batch table) and Attachments are intentionally omitted —
// those flow in from the selected batches and the file-upload UI, not this fixture.
export const MOCK_SCHEDULE_FORM_DATA = {
  // Schedule Pickup Date/Time intentionally omitted — user selects these in the form.
  // Pickup Details
  pickupOrganizationName: 'Resilient Life Sciences',
  pickupContactName:      'Smita Jaykar',
  pickupContactNumber:    '+353 0872797709',
  pickupContactEmail:     'info@resilient.com',
  pickupStreetAddress1:   'Block 7, City North Business Campus',
  pickupStreetAddress2:   'Stamullen',
  pickupCity:             'Co Meath',
  pickupState:            'Meath',
  pickupPostalCode:       'K32 YD60',
  pickupCountry:          'Ireland',
  // Sponsor Point of Contact Details
  siteUserName:    'William Johnson',
  siteUserEmail:   'william.johnson@treetops.com',
  siteUserContact: '+1 201-901-0412',
  // Delivery Address Details
  deliveryOrganizationName: 'UPS',
  deliveryContactName:      'William Johnson',
  deliveryContactNumber:    '+31 9485948594',
  deliveryContactEmail:     'helpdesk@ups.com',
  deliveryStreetAddress1:   'United Parcel Service Nederland B.V',
  deliveryStreetAddress2:   'Achtseweg Noord 14-16',
  deliveryCity:             'Eindhoven',
  deliveryState:            '-',
  deliveryPostalCode:       '5651',
  deliveryCountry:          'Netherlands',
  expectedDeliveryDate:     '16-AUG-2025',
  specialInstructions:      'NA',
  // Contact Information for Shipment
  freightForwarderUserName: 'Ashish Dalal',
  freightForwarderContact:  '+353 9485948594',
  billingCentreForShipment: '30029010',
  freightForwarderEmail:    'YES',
  // Config / system-supplied defaults — see CHR_OrderCreate_Form_Mapping.xlsx.
  // Not collected on the form; feed the config/constant tags of the Order Create XML.
  requestingCompanyName: 'Oxford Biomedica', // customer <companyName>
  sponsorCompanyName:    'BMS',              // billTo   <companyName>
  currencyCode:          'USD',              // billTo   <currencyCode>
  jobNumber:             'OXB05092025',      // top-level referenceNumber JNO
  pickupLocationId:      'OXB-DURHAM-MFG01', // origin      <customerLocationId>
  deliveryLocationId:    'UPS-NL-EINDHOVEN', // destination <customerLocationId>
};

// "Shipment Order Numbers Generated" block from the same tab — shown in the
// post-submit success dialog (Sponsor / CMO / Freight Forwarder IDs).
export const MOCK_SHIPMENT_IDS = {
  sponsorShipmentID:          'PKG428-25F',
  vendorShipmentID:           'RAD05092025',
  freightForwarderScheduleID: '3919519200',
};

